import { JournalEntry, ValidationEvent } from "@/types";

const isBuildBlockingError = (v: ValidationEvent): boolean =>
  v.severity === "error" && v.field === "payoutId";

/**
 * Errors the server attaches while building the journal from Salesforce data
 * (a blank Payout_ID__c on a CBA Stripe opportunity). They cannot be derived
 * from the entries a user edits, so they are read from the built entries and
 * only a rebuild clears them.
 */
export function getBuildBlockingErrors(builtEntries: JournalEntry[]): string[] {
  const messages = builtEntries
    .flatMap((entry) => entry.validations ?? [])
    .filter(isBuildBlockingError)
    .map((v) => v.message);
  return Array.from(new Set(messages));
}

/**
 * A saved draft carries the validations of the build it was saved from, which
 * may predate a fix in Salesforce. The current build is the only source of
 * truth for these errors, so the draft's copies are dropped and the current
 * build's are attached instead.
 */
export function withCurrentBuildErrors<T extends JournalEntry>(
  draftEntries: T[],
  builtEntries: JournalEntry[],
): T[] {
  const current = getBuildBlockingErrors(builtEntries).map((message): ValidationEvent => ({
    severity: "error",
    message,
    field: "payoutId",
  }));
  return draftEntries.map((entry) => ({
    ...entry,
    validations: [...(entry.validations ?? []).filter((v) => !isBuildBlockingError(v)), ...current],
  }));
}
